// Mock data for UI-only version of the Purchase Order management application

export interface User {
  id: number;
  username: string;
  name: string;
  role: "Manager" | "Planning Team" | "Appointment Team" | "Sales Order Team" | "Courier Team" | "Invoicing Team" | "Warehouse Team";
  department?: string;
}

export interface PurchaseOrder {
  id: number;
  client: string;
  platform: string;
  poNumber: string;
  brand: string;
  supplier: string;
  category: string;
  totalAmount: number;
  status: string;

  teamAssigned: string;
  estimatedDelivery: Date;
  workflowProgress: number;
  createdAt: Date;
  updatedAt: Date;
  deliveryAddress?: string;
  notes?: string;
  location: string;

  client_Name: string;

  // Excel-style editable fields by team
  // Planner Team fields
  sanityCheck: string;
  inventoryCheck: string;
  fcn: string;
  fillRate: string;
  reasonCode: string;

  // Appointment Team fields
  appointmentStatus: string;
  physicalAppointmentDate: string;
  inwardDateRequested: string;
  reappointmentDate: string;

  // Sales Order Team fields
  soProcessed: string;
  soStatus: string;
  poSentToParty: string;
  soClosed: string;
  
  PlannerStatus: string;

  // Courier Team fields
  pickupDate: string;
  courierPickupFlag: string;
  pickupIntimationSent: string;
  lrNumber: string;
  reminderSent: string;
  unloaded: string;
  podInSap: string;
  courierRecall: string;
  deliveryStatus: string;

  // Invoicing Team fields
  invoiceNumber: string;
  invoiceValue: number;
  sharedToWarehouse: string;

  // Warehouse Team fields
  picklistGeneration: string;
  pickAndPack: string;
  readyToDispatch: string;
  handoverToCourier: string;

  // status fields by team:
  AppointmentStatus?: string;
  SalesOrderStatus?: string;
  CourierStatus?: string;
  InvoiceStatus?: string;
  WarehouseStatus?: string;
}

export interface POItem {
  id: number;
  poId: number;
  itemName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  description?: string;
  createdAt: Date;
}

export interface ValidationIssue {
  id: number;
  poId: number;
  field: string;
  issue: string;
  severity: "high" | "medium" | "low";
  createdAt: Date;
}

export interface AuditLog {
  id: number;
  userId: number;
  userName: string;
  action: string;
  entityType: string;
  entityId: number;
  changes?: string;
  timestamp: Date;
}

// Mock Purchase Orders
export const mockPurchaseOrders: any[] = [
  {
    id: 1,
    poNumber: "PO-100046",
    brand: "Johnson & Johnson",
    supplier: "MedSupply Corp",
    category: "Baby Care",
    totalAmount: 45000,
    status: "Validation Pending",
    teamAssigned: "Planning",
    estimatedDelivery: new Date("2024-07-15"),
    workflowProgress: 35,
    createdAt: new Date("2024-06-01"),
    updatedAt: new Date("2024-06-10"),
    deliveryAddress: "123 Warehouse St, City, State 12345",
    notes: "Urgent shipment for summer season",
    location: "New York",

    // Planner Team fields - IN PROGRESS
    sanityCheck: "In Progress",
    inventoryCheck: "In Progress",
    fcn: "FCN-001",
    fillRate: "85%",
    reasonCode: "RC-100",

    // All other teams - Empty until Planner completes
    appointmentStatus: "",
    physicalAppointmentDate: "",
    inwardDateRequested: "",
    reappointmentDate: "",
    soProcessed: "",
    soStatus: "",
    poSentToParty: "",
    soClosed: "",
    pickupDate: "",
    courierPickupFlag: "",
    pickupIntimationSent: "",
    lrNumber: "",
    reminderSent: "",
    unloaded: "",
    podInSap: "",
    courierRecall: "",
    deliveryStatus: "",
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "",
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
  {
    id: 2,
    poNumber: "8524110",
    brand: "Procter & Gamble",
    supplier: "P&G Direct",
    category: "Personal Care",
    totalAmount: 32000,
    status: "In Progress",
    teamAssigned: "Sales Order",
    estimatedDelivery: new Date("2024-07-20"),
    workflowProgress: 45,
    createdAt: new Date("2024-06-05"),
    updatedAt: new Date("2024-06-10"),
    deliveryAddress: "456 Distribution Center, City, State 67890",
    notes: "",
    location: "California",

    // Planner Team fields - COMPLETED
    sanityCheck: "Complete",
    inventoryCheck: "Complete",
    fcn: "FCN-002",
    fillRate: "92%",
    reasonCode: "RC-101",

    // Appointment Team fields - COMPLETED
    appointmentStatus: "Complete",
    physicalAppointmentDate: "2024-07-18",
    inwardDateRequested: "2024-07-15",
    reappointmentDate: "",

    // Sales Order Team fields - IN PROGRESS
    soProcessed: "Yes",
    soStatus: "Processing",
    poSentToParty: "Yes",
    soClosed: "No",

    // All other teams - Empty until Sales Order completes
    pickupDate: "",
    courierPickupFlag: "",
    pickupIntimationSent: "",
    lrNumber: "",
    reminderSent: "",
    unloaded: "",
    podInSap: "",
    courierRecall: "",
    deliveryStatus: "",
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "",
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
  {
    id: 3,
    poNumber: "PO-100048",
    brand: "Unilever",
    supplier: "Unilever Supply Chain",
    category: "Home Care",
    totalAmount: 28500,
    status: "Ready for Allocation",
    teamAssigned: "Courier",
    estimatedDelivery: new Date("2024-07-25"),
    workflowProgress: 60,
    createdAt: new Date("2024-06-08"),
    updatedAt: new Date("2024-06-12"),
    deliveryAddress: "789 Retail Hub, City, State 54321",
    notes: "Seasonal promotion items",
    location: "Texas",

    // Planner Team fields - COMPLETED
    sanityCheck: "Complete",
    inventoryCheck: "Complete",
    fcn: "FCN-003",
    fillRate: "78%",
    reasonCode: "RC-102",

    // Appointment Team fields - COMPLETED
    appointmentStatus: "Complete",
    physicalAppointmentDate: "2024-07-22",
    inwardDateRequested: "2024-07-20",
    reappointmentDate: "",

    // Sales Order Team fields - COMPLETED
    soProcessed: "Yes",
    soStatus: "Complete",
    poSentToParty: "Yes",
    soClosed: "Yes",

    // Courier Team fields - IN PROGRESS
    pickupDate: "2024-07-24",
    courierPickupFlag: "Scheduled",
    pickupIntimationSent: "Yes",
    lrNumber: "LR-2024-003",
    reminderSent: "No",
    unloaded: "No",
    podInSap: "Pending",
    courierRecall: "No",
    deliveryStatus: "In Transit",

    // All other teams - Empty until Courier completes
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "",
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
  {
    id: 4,
    poNumber: "PO-100049",
    brand: "Nestle",
    supplier: "Nestle Distribution",
    category: "Food & Beverages",
    totalAmount: 52000,
    status: "Completed",
    teamAssigned: "Warehouse",
    estimatedDelivery: new Date("2024-07-30"),
    workflowProgress: 85,
    createdAt: new Date("2024-06-12"),
    updatedAt: new Date("2024-06-15"),
    deliveryAddress: "321 Food Plaza, City, State 98765",
    notes: "Temperature controlled storage required",
    location: "Florida",

    // Planner Team fields
    sanityCheck: "Complete",
    inventoryCheck: "Complete",
    fcn: "FCN-004",
    fillRate: "95%",
    reasonCode: "RC-103",

    // Appointment Team fields
    appointmentStatus: "Complete",
    physicalAppointmentDate: "2024-07-28",
    inwardDateRequested: "2024-07-25",
    reappointmentDate: "",

    // Sales Order Team fields
    soProcessed: "Yes",
    soStatus: "Complete",
    poSentToParty: "Yes",
    soClosed: "Yes",

    // Courier Team fields
    pickupDate: "2024-07-29",
    courierPickupFlag: "Complete",
    pickupIntimationSent: "Yes",
    lrNumber: "LR-2024-004",
    reminderSent: "No",
    unloaded: "Yes",
    podInSap: "Complete",
    courierRecall: "No",
    deliveryStatus: "Delivered",

    // Invoicing Team fields
    invoiceNumber: "INV-2024-004",
    invoiceValue: 52000,
    sharedToWarehouse: "Yes",

    // Warehouse Team fields
    picklistGeneration: "Complete",
    pickAndPack: "Complete",
    readyToDispatch: "Yes",
    handoverToCourier: "Complete",
  },
  {
    id: 5,
    poNumber: "PO-100050",
    brand: "L'Oreal",
    supplier: "L'Oreal Professional",
    category: "Beauty & Cosmetics",
    totalAmount: 38000,
    status: "Draft",
    teamAssigned: "Planning",
    estimatedDelivery: new Date("2024-08-05"),
    workflowProgress: 15,
    createdAt: new Date("2024-06-15"),
    updatedAt: new Date("2024-06-18"),
    deliveryAddress: "654 Beauty Center, City, State 13579",
    notes: "New product launch items",
    location: "Illinois",

    // Planner Team fields - Only Planner fields populated as this PO is still in Planning stage
    sanityCheck: "In Progress",
    inventoryCheck: "Pending",
    fcn: "FCN-005",
    fillRate: "65%",
    reasonCode: "RC-104",

    // All other teams - Empty until Planner completes
    appointmentStatus: "",
    physicalAppointmentDate: "",
    inwardDateRequested: "",
    reappointmentDate: "",
    soProcessed: "",
    soStatus: "",
    poSentToParty: "",
    soClosed: "",
    pickupDate: "",
    courierPickupFlag: "",
    pickupIntimationSent: "",
    lrNumber: "",
    reminderSent: "",
    unloaded: "",
    podInSap: "",
    courierRecall: "",
    deliveryStatus: "",
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "",
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
  {
    id: 6,
    poNumber: "PO-100051",
    brand: "Himalaya",
    supplier: "Himalaya Wellness",
    category: "Healthcare",
    totalAmount: 42500,
    status: "Validation Pending",
    teamAssigned: "Appointment",
    estimatedDelivery: new Date("2024-08-10"),
    workflowProgress: 25,
    createdAt: new Date("2024-06-20"),
    updatedAt: new Date("2024-06-20"),
    deliveryAddress: "987 Health Plaza, City, State 24680",
    notes: "Ayurvedic products for monsoon season",
    location: "Maharashtra",
    isNew: true,

    // Planner Team fields - COMPLETED
    sanityCheck: "Complete",
    inventoryCheck: "Complete",
    fcn: "FCN-006",
    fillRate: "70%",
    reasonCode: "RC-105",

    // Appointment Team fields - IN PROGRESS
    appointmentStatus: "In Progress",
    physicalAppointmentDate: "2024-08-08",
    inwardDateRequested: "2024-08-08",
    reappointmentDate: "",

    // All other teams - Empty until Appointment completes
    soProcessed: "",
    soStatus: "",
    poSentToParty: "",
    soClosed: "",
    pickupDate: "",
    courierPickupFlag: "",
    pickupIntimationSent: "",
    lrNumber: "",
    reminderSent: "",
    unloaded: "",
    podInSap: "",
    courierRecall: "",
    deliveryStatus: "",
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "",
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
  {
    id: 7,
    poNumber: "PO-100052",
    brand: "Dabur",
    supplier: "Dabur India Ltd",
    category: "FMCG",
    totalAmount: 36800,
    status: "In Progress",
    teamAssigned: "Sales Order",
    estimatedDelivery: new Date("2024-08-15"),
    workflowProgress: 40,
    createdAt: new Date("2024-06-22"),
    updatedAt: new Date("2024-06-22"),
    deliveryAddress: "456 FMCG Hub, City, State 97531",
    notes: "Traditional medicine products",
    location: "Delhi",
    isNew: true,

    // Planner Team fields - COMPLETED
    sanityCheck: "Complete",
    inventoryCheck: "Complete",
    fcn: "FCN-007",
    fillRate: "88%",
    reasonCode: "RC-106",

    // Appointment Team fields - COMPLETED
    appointmentStatus: "Complete",
    physicalAppointmentDate: "2024-08-12",
    inwardDateRequested: "2024-08-10",
    reappointmentDate: "",

    // Sales Order Team fields - IN PROGRESS
    soProcessed: "Yes",
    soStatus: "Processing",
    poSentToParty: "Yes",
    soClosed: "No",

    // All other teams - Empty until Sales Order completes
    pickupDate: "",
    courierPickupFlag: "",
    pickupIntimationSent: "",
    lrNumber: "",
    reminderSent: "",
    unloaded: "",
    podInSap: "",
    courierRecall: "",
    deliveryStatus: "",
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "",
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
  {
    id: 8,
    poNumber: "PO-100053",
    brand: "Marico",
    supplier: "Marico Limited",
    category: "Personal Care",
    totalAmount: 29750,
    status: "Ready for Allocation",
    teamAssigned: "Courier",
    estimatedDelivery: new Date("2024-08-18"),
    workflowProgress: 55,
    createdAt: new Date("2024-06-23"),
    updatedAt: new Date("2024-06-23"),
    deliveryAddress: "123 Beauty Point, City, State 86420",
    notes: "Hair care and skin care products",
    location: "Mumbai",
    isNew: true,

    // Planner Team fields - COMPLETED
    sanityCheck: "Complete",
    inventoryCheck: "Complete",
    fcn: "FCN-008",
    fillRate: "91%",
    reasonCode: "RC-107",

    // Appointment Team fields - COMPLETED
    appointmentStatus: "Complete",
    physicalAppointmentDate: "2024-08-16",
    inwardDateRequested: "2024-08-14",
    reappointmentDate: "",

    // Sales Order Team fields - COMPLETED
    soProcessed: "Yes",
    soStatus: "Complete",
    poSentToParty: "Yes",
    soClosed: "Yes",

    // Courier Team fields - IN PROGRESS
    pickupDate: "2024-08-17",
    courierPickupFlag: "Scheduled",
    pickupIntimationSent: "Yes",
    lrNumber: "LR-2024-006",
    reminderSent: "No",
    unloaded: "No",
    podInSap: "Pending",
    courierRecall: "No",
    deliveryStatus: "In Transit",

    // All other teams - Empty until Courier completes
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "",
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
  {
    id: 9,
    poNumber: "PO-100054",
    brand: "Godrej",
    supplier: "Godrej Consumer Products",
    category: "Home Care",
    totalAmount: 47200,
    status: "In Progress",
    teamAssigned: "Courier",
    estimatedDelivery: new Date("2024-08-20"),
    workflowProgress: 65,
    createdAt: new Date("2024-06-24"),
    updatedAt: new Date("2024-06-24"),
    deliveryAddress: "789 Home Center, City, State 15973",
    notes: "Household cleaning products",
    location: "Gujarat",
    isNew: true,

    // Planner Team fields - COMPLETED
    sanityCheck: "Complete",
    inventoryCheck: "Complete",
    fcn: "FCN-009",
    fillRate: "83%",
    reasonCode: "RC-108",

    // Appointment Team fields - COMPLETED
    appointmentStatus: "Complete",
    physicalAppointmentDate: "2024-08-18",
    inwardDateRequested: "2024-08-16",
    reappointmentDate: "",

    // Sales Order Team fields - COMPLETED
    soProcessed: "Yes",
    soStatus: "Complete",
    poSentToParty: "Yes",
    soClosed: "Yes",

    // Courier Team fields - IN PROGRESS
    pickupDate: "2024-08-19",
    courierPickupFlag: "Scheduled",
    pickupIntimationSent: "Yes",
    lrNumber: "LR-2024-007",
    reminderSent: "No",
    unloaded: "No",
    podInSap: "Pending",
    courierRecall: "No",
    deliveryStatus: "In Transit",

    // All other teams - Empty until Courier completes
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "",
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
  {
    id: 10,
    poNumber: "PO-100055",
    brand: "Patanjali",
    supplier: "Patanjali Ayurved",
    category: "Natural Products",
    totalAmount: 33600,
    status: "Validation Pending",
    teamAssigned: "Planning",
    estimatedDelivery: new Date("2024-08-25"),
    workflowProgress: 20,
    createdAt: new Date("2024-06-25"),
    updatedAt: new Date("2024-06-25"),
    deliveryAddress: "321 Organic Plaza, City, State 75319",
    notes: "Organic and natural product range",
    location: "Haryana",
    isNew: true,

    // Planner Team fields
    sanityCheck: "Pending",
    inventoryCheck: "Pending",
    fcn: "FCN-010",
    fillRate: "77%",
    reasonCode: "RC-109",

    // Appointment Team fields
    appointmentStatus: "Pending",
    physicalAppointmentDate: "",
    inwardDateRequested: "2024-08-22",
    reappointmentDate: "",

    // Sales Order Team fields
    soProcessed: "No",
    soStatus: "Pending",
    poSentToParty: "No",
    soClosed: "No",

    // Courier Team fields
    pickupDate: "",
    courierPickupFlag: "Pending",
    pickupIntimationSent: "No",
    lrNumber: "",
    reminderSent: "No",
    unloaded: "No",
    podInSap: "Pending",
    courierRecall: "No",
    deliveryStatus: "Pending",

    // Invoicing Team fields
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "No",

    // Warehouse Team fields
    picklistGeneration: "Pending",
    pickAndPack: "Pending",
    readyToDispatch: "No",
    handoverToCourier: "Pending",
  },
  {
    id: 11,
    poNumber: "PO-100056",
    brand: "Bajaj",
    supplier: "Bajaj Corp",
    category: "Personal Care",
    totalAmount: 41300,
    status: "Draft",
    teamAssigned: "Planning",
    estimatedDelivery: new Date("2024-08-28"),
    workflowProgress: 10,
    createdAt: new Date("2024-06-26"),
    updatedAt: new Date("2024-06-26"),
    deliveryAddress: "654 Personal Care Hub, City, State 95148",
    notes: "Hair oil and personal care items",
    location: "Rajasthan",
    isNew: true,

    // Planner Team fields
    sanityCheck: "Pending",
    inventoryCheck: "Pending",
    fcn: "FCN-011",
    fillRate: "68%",
    reasonCode: "RC-110",

    // Appointment Team fields
    appointmentStatus: "Pending",
    physicalAppointmentDate: "",
    inwardDateRequested: "2024-08-25",
    reappointmentDate: "",

    // Sales Order Team fields
    soProcessed: "No",
    soStatus: "Pending",
    poSentToParty: "No",
    soClosed: "No",

    // Courier Team fields
    pickupDate: "",
    courierPickupFlag: "Pending",
    pickupIntimationSent: "No",
    lrNumber: "",
    reminderSent: "No",
    unloaded: "No",
    podInSap: "Pending",
    courierRecall: "No",
    deliveryStatus: "Pending",

    // Invoicing Team fields
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "No",

    // Warehouse Team fields
    picklistGeneration: "Pending",
    pickAndPack: "Pending",
    readyToDispatch: "No",
    handoverToCourier: "Pending",
  },
  {
    id: 12,
    poNumber: "PO-100057",
    brand: "Wipro",
    supplier: "Wipro Consumer Care",
    category: "Personal Care",
    totalAmount: 39800,
    status: "In Progress",
    teamAssigned: "Appointment",
    estimatedDelivery: new Date("2024-08-30"),
    workflowProgress: 35,
    createdAt: new Date("2024-06-27"),
    updatedAt: new Date("2024-06-27"),
    deliveryAddress: "987 Tech Plaza, City, State 35792",
    notes: "Personal hygiene products",
    location: "Karnataka",
    isNew: true,

    // Planner Team fields
    sanityCheck: "Complete",
    inventoryCheck: "In Progress",
    fcn: "FCN-012",
    fillRate: "85%",
    reasonCode: "RC-111",

    // Appointment Team fields
    appointmentStatus: "Scheduled",
    physicalAppointmentDate: "2024-08-28",
    inwardDateRequested: "2024-08-26",
    reappointmentDate: "",

    // Sales Order Team fields
    soProcessed: "No",
    soStatus: "Pending",
    poSentToParty: "No",
    soClosed: "No",

    // Courier Team fields
    pickupDate: "",
    courierPickupFlag: "Pending",
    pickupIntimationSent: "No",
    lrNumber: "",
    reminderSent: "No",
    unloaded: "No",
    podInSap: "Pending",
    courierRecall: "No",
    deliveryStatus: "Pending",

    // Invoicing Team fields
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "No",

    // Warehouse Team fields
    picklistGeneration: "Pending",
    pickAndPack: "Pending",
    readyToDispatch: "No",
    handoverToCourier: "Pending",
  },
  {
    id: 13,
    poNumber: "PO-100061",
    brand: "Patanjali",
    supplier: "Patanjali Ayurved",
    category: "Ayurvedic",
    totalAmount: 31500,
    status: "Delayed",
    teamAssigned: "Invoice",
    estimatedDelivery: new Date("2024-08-25"),
    workflowProgress: 85,
    createdAt: new Date("2024-06-10"), // Created 3+ days ago to show delay
    updatedAt: new Date("2024-06-10"),
    deliveryAddress: "890 Wellness Center, City, State 45678",
    notes: "Natural health products - DELAYED at Invoice stage",
    location: "Haridwar",
    isNew: false,

    // Planner Team fields - COMPLETED
    sanityCheck: "Complete",
    inventoryCheck: "Complete",
    fcn: "FCN-013",
    fillRate: "95%",
    reasonCode: "RC-113",

    // Appointment Team fields - COMPLETED
    appointmentStatus: "Complete",
    physicalAppointmentDate: "2024-08-20",
    inwardDateRequested: "2024-08-18",
    reappointmentDate: "",

    // Sales Order Team fields - COMPLETED
    soProcessed: "Yes",
    soStatus: "Complete",
    poSentToParty: "Yes",
    soClosed: "Yes",

    // Courier Team fields - COMPLETED
    pickupDate: "2024-08-21",
    courierPickupFlag: "Complete",
    pickupIntimationSent: "Yes",
    lrNumber: "LR-2024-013",
    reminderSent: "Yes",
    unloaded: "Yes",
    podInSap: "Updated",
    courierRecall: "No",
    deliveryStatus: "Delivered",

    // Invoice Team fields - DELAYED (taking more than 2 days)
    invoiceNumber: "",
    invoiceValue: 0,
    sharedToWarehouse: "No",

    // Warehouse Team - Empty until Invoice completes
    picklistGeneration: "",
    pickAndPack: "",
    readyToDispatch: "",
    handoverToCourier: "",
  },
];

// Mock PO Items
export const mockPOItems: POItem[] = [
  {
    id: 1,
    poId: 1,
    itemName: "Johnson's Baby Shampoo",
    sku: "JJ-BS-500ML",
    quantity: 100,
    unitPrice: 250,
    totalPrice: 25000,
    description: "500ml gentle baby shampoo",
    createdAt: new Date("2024-06-01"),
  },
  {
    id: 2,
    poId: 1,
    itemName: "Johnson's Baby Oil",
    sku: "JJ-BO-200ML",
    quantity: 80,
    unitPrice: 200,
    totalPrice: 16000,
    description: "200ml baby oil",
    createdAt: new Date("2024-06-01"),
  },
];

// Mock Validation Issues
export const mockValidationIssues: ValidationIssue[] = [
  {
    id: 1,
    poId: 1,
    field: "totalAmount",
    issue: "Amount exceeds approved budget limit",
    severity: "high",
    createdAt: new Date("2024-06-10"),
  },
  {
    id: 2,
    poId: 2,
    field: "deliveryAddress",
    issue: "Incomplete delivery address",
    severity: "medium",
    createdAt: new Date("2024-06-10"),
  },
];

// Mock Audit Logs
export const mockAuditLogs: AuditLog[] = [
  {
    id: 1,
    userId: 1,
    userName: "Admin Manager",
    action: "Created PO",
    entityType: "PurchaseOrder",
    entityId: 1,
    changes: "Created new purchase order PO-100046",
    timestamp: new Date("2024-06-01T10:00:00"),
  },
  {
    id: 2,
    userId: 2,
    userName: "John Planner",
    action: "Updated PO",
    entityType: "PurchaseOrder",
    entityId: 1,
    changes: "Updated sanity check status to Pending",
    timestamp: new Date("2024-06-02T14:30:00"),
  },
  {
    id: 3,
    userId: 3,
    userName: "Sarah Scheduler",
    action: "Scheduled Appointment",
    entityType: "PurchaseOrder",
    entityId: 1,
    changes: "Scheduled physical appointment for 2024-07-12",
    timestamp: new Date("2024-06-03T09:15:00"),
  },
];

// Helper functions for filtering and searching
export const filterPOsByStatus = (status: string): PurchaseOrder[] => {
  return mockPurchaseOrders.filter((po) => po.status === status);
};

export const searchPOs = (query: string): PurchaseOrder[] => {
  const lowercaseQuery = query.toLowerCase();
  return mockPurchaseOrders.filter(
    (po) =>
      po.poNumber.toLowerCase().includes(lowercaseQuery) ||
      po.brand.toLowerCase().includes(lowercaseQuery) ||
      po.supplier.toLowerCase().includes(lowercaseQuery) ||
      po.category.toLowerCase().includes(lowercaseQuery)
  );
};

export const getPOById = (id: number): PurchaseOrder | undefined => {
  return mockPurchaseOrders.find((po) => po.id === id);
};

export const getItemsByPOId = (poId: number): POItem[] => {
  return mockPOItems.filter((item) => item.poId === poId);
};

export const getValidationIssuesByPOId = (poId: number): ValidationIssue[] => {
  return mockValidationIssues.filter((issue) => issue.poId === poId);
};

export const getAuditLogsByEntityId = (entityId: number): AuditLog[] => {
  return mockAuditLogs.filter((log) => log.entityId === entityId);
};

// MockDataService class to handle all mock API operations
export class MockDataService {
  static getDashboardStats() {
    return {
      totalPOs: mockPurchaseOrders.length,
      totalPOsChange: "+12%",
      pendingValidation: mockPurchaseOrders.filter((po) => po.status === "Validation Pending").length,
      readyForAllocation: mockPurchaseOrders.filter((po) => po.status === "Ready for Allocation").length,
      completedPOs: mockPurchaseOrders.filter((po) => po.status === "Completed").length,
      completedPOsChange: "+8%",
      workflowProgress: 65,
    };
  }

  static getPurchaseOrders(page: number = 1, limit: number = 10, search: string = "", status: string = "") {
    let filtered = mockPurchaseOrders;

    if (search) {
      filtered = filtered.filter(
        (po) =>
          po.poNumber.toLowerCase().includes(search.toLowerCase()) ||
          po.brand.toLowerCase().includes(search.toLowerCase()) ||
          po.supplier.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (status) {
      filtered = filtered.filter((po) => po.status === status);
    }

    const start = (page - 1) * limit;
    const end = start + limit;

    return {
      items: filtered.slice(start, end),
      total: filtered.length,
      page,
      limit,
    };
  }

  static getPendingValidationPOs() {
    return mockPurchaseOrders.filter((po) => po.status === "Validation Pending");
  }

  static getPurchaseOrderById(id: number) {
    return mockPurchaseOrders.find((po) => po.id === id);
  }

  static getPOItems(poId: number) {
    return mockPOItems.filter((item) => item.poId === poId);
  }

  static getValidationIssues(poId: number) {
    return mockValidationIssues.filter((issue) => issue.poId === poId);
  }

  static getInventoryItems(page: number = 1, limit: number = 10, search: string = "", status: string = "") {
    // Mock inventory data
    const mockInventory = [
      { id: 1, sku: "JJ-BS-500ML", name: "Johnson's Baby Shampoo", quantity: 150, status: "In Stock" },
      { id: 2, sku: "JJ-BO-200ML", name: "Johnson's Baby Oil", quantity: 25, status: "Low Stock" },
    ];

    let filtered = mockInventory;

    if (search) {
      filtered = filtered.filter(
        (item) => item.name.toLowerCase().includes(search.toLowerCase()) || item.sku.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (status) {
      filtered = filtered.filter((item) => item.status === status);
    }

    const start = (page - 1) * limit;
    const end = start + limit;

    return {
      items: filtered.slice(start, end),
      total: filtered.length,
      page,
      limit,
    };
  }

  static getLowStockItems() {
    return [{ id: 2, sku: "JJ-BO-200ML", name: "Johnson's Baby Oil", quantity: 25, minStock: 50, status: "Low Stock" }];
  }

  static getAuditLogs(page: number = 1, limit: number = 10, userId?: number, teamName?: string) {
    let filtered = mockAuditLogs;

    if (userId) {
      filtered = filtered.filter((log) => log.userId === userId);
    }

    if (teamName) {
      filtered = filtered.filter((log) => log.userName.toLowerCase().includes(teamName.toLowerCase()));
    }

    const start = (page - 1) * limit;
    const end = start + limit;

    return {
      items: filtered.slice(start, end),
      total: filtered.length,
      page,
      limit,
    };
  }

  static getRecentAuditLogs(limit: number = 10) {
    return mockAuditLogs.slice(0, limit);
  }

  static getTeamAuditLogs(teamName: string) {
    return mockAuditLogs.filter((log) => log.userName.toLowerCase().includes(teamName.toLowerCase()));
  }

  static getReports(timeRange: string, category?: string, brand?: string) {
    return {
      poStatus: [
        { name: "Completed", value: 35 },
        { name: "In Progress", value: 25 },
        { name: "Pending", value: 20 },
        { name: "Validation", value: 20 },
      ],
      poVolume: [
        { name: "Jan", completed: 12, pending: 8 },
        { name: "Feb", completed: 15, pending: 6 },
        { name: "Mar", completed: 18, pending: 4 },
        { name: "Apr", completed: 22, pending: 7 },
      ],
      brandDistribution: [
        { name: "Johnson & Johnson", value: 30 },
        { name: "Procter & Gamble", value: 25 },
        { name: "Unilever", value: 20 },
        { name: "Nestle", value: 15 },
        { name: "L'Oreal", value: 10 },
      ],
      fulfillmentRate: [
        { name: "Jan", rate: 85 },
        { name: "Feb", rate: 88 },
        { name: "Mar", rate: 92 },
        { name: "Apr", rate: 90 },
      ],
    };
  }
}
